import Web3 from "web3"
import { tokenSymbol2token } from "../../../../../config/tokens"
import { TokenInfoFormatted } from "../../../../../hooks/useTokenListFormatted"
import { LiquidityManagerContract } from "../../../../../types/abis/iZiSwap/LiquidityManager"
import { ChainId, TokenSymbol } from "../../../../../types/mod"
import { getLiquidityManagerContract, getPoolContractByAddress } from "../../../../../utils/contractFactory"
import { toContractFeeNumber } from "../../../../../utils/funcs"
import { getSwapTokenAddress } from "../../../common/positionPoolHelper"
import { BasePreQueryPlugin } from "../BaseDexPlugin"
import { DagNode, Path, PreQueryResult } from "../utils"
import { iZiPreQueryResult } from './types'
import { LOW_CHANCE_POOL, LOW_CHANGE, MULTIHOP_MID_TOKEN_SYMBOL, STABLE_COIN_SYMBOL, SUPPORTED_001_POOL, SupportFeeTiers } from "./config"
import { LIQUIDITY_MANAGER_ADDRESS } from "../../../../../config/trade/tradeContracts"
import { decodeMethodResult } from "../../../../../utils/contractHelpers"
import { Contract } from 'web3-eth-contract'
import { isAddress } from "@ethersproject/address"
import BigNumber from "bignumber.js"
import { PoolContract, StateResponse } from "../../../../../types/abis/iZiSwap/Pool"


interface PoolPair {
    tokenA: TokenInfoFormatted
    tokenB: TokenInfoFormatted
    feeContractNumber: number
}


interface Link {
    tokenB: TokenInfoFormatted;
    feeContractNumber: number;
}

export class iZiSwapPreQueryPlugin extends BasePreQueryPlugin {

    private chainId: ChainId
    private liquidityManagerContract: LiquidityManagerContract
    private liquidityManagerAddress: string
    // only for encode abi of calling and decode method result
    private fakePoolContract: PoolContract

    private pairsOfCalling: PoolPair[] = undefined as unknown as PoolPair[]
    private knownPairs: PoolPair[] = undefined as unknown as PoolPair[]
    private knownPoolAddress: string[] = undefined as unknown as string[]


    private knownPoolPoint: number[] = undefined as unknown as number[]
    private unknownPoolPoint: number[] = undefined as unknown as number[]
    
    private allLinks: Link[] = undefined as unknown as Link[]

    private dagNodes: DagNode[] = undefined as unknown as DagNode[]
    private responsePoolAddress: string[] = undefined as unknown as string[]

    private tokenA: TokenInfoFormatted = undefined as unknown as TokenInfoFormatted
    private tokenB: TokenInfoFormatted = undefined as unknown as TokenInfoFormatted

    public constructor(preQueryResult: iZiPreQueryResult, chainId: ChainId, web3: Web3) {
        super(preQueryResult)
        this.chainId = chainId
        this.liquidityManagerContract = getLiquidityManagerContract(chainId, web3) as LiquidityManagerContract 
        this.liquidityManagerAddress = LIQUIDITY_MANAGER_ADDRESS[chainId]
        this.fakePoolContract = getPoolContractByAddress(chainId, web3, this.liquidityManagerAddress) as unknown as PoolContract
    }

    private getSwapPoolKey(tokenA: TokenInfoFormatted, tokenB: TokenInfoFormatted, feeContractNumber: number) : string {
        const tokenASymbol = tokenA.symbol.toUpperCase()
        const tokenBSymbol = tokenB.symbol.toUpperCase()
        if (tokenASymbol < tokenBSymbol) {
            return tokenASymbol + '-' + tokenBSymbol + '-' + String(feeContractNumber)
        } else {
            return tokenBSymbol + '-' + tokenASymbol + '-' + String(feeContractNumber)
        }
    }

    private getSwapPoolKeySymbol(tokenASymbol: string, tokenBSymbol: string, feeContractNumber: number): string {
        const a = tokenASymbol.toUpperCase()
        const b = tokenBSymbol.toUpperCase()
        if (a < b) {
            return a + '-' + b + '-' + String(feeContractNumber)
        } else {
            return b + '-' + a + '-' + String(feeContractNumber)
        }
    }

    private getNormalSymbol(symbol: string): string {
        if (symbol === 'ETH') {
            return 'WETH'
        }
        if (symbol === 'ETC') {
            return 'WETC'
        }
        if (symbol === 'WBNB') {
            return 'BNB'
        }
        return symbol
    }

    private hasPool(tokenA: TokenInfoFormatted, tokenB: TokenInfoFormatted, feeContractNumber: number) : boolean {
        const lastPreQueryResult = this.preQueryResult as iZiPreQueryResult
        if (this.chainId != lastPreQueryResult.lastChainId) {
            return false
        }
        const key = this.getSwapPoolKey(tokenA, tokenB, feeContractNumber)
        return lastPreQueryResult.pool.has(key)
    }

    override getPreQueryDag(tokenA: TokenInfoFormatted, tokenB: TokenInfoFormatted) : DagNode[] {

        this.tokenA = tokenA
        this.tokenB = tokenB

        const tokenBList: TokenInfoFormatted[] = []
        const tokenBSymbols = MULTIHOP_MID_TOKEN_SYMBOL[this.chainId] ?? []

        tokenBSymbols.forEach((s)=>{
            if (s === tokenA.symbol || s === tokenB.symbol) {
                return
            }
            const token = tokenSymbol2token(s, this.chainId)
            if (token.address) {
                tokenBList.push(token)
            }
        })

        this.allLinks = []

        const supportFeeTiers = SupportFeeTiers[this.chainId] ?? []
        for (const tokenB of tokenBList) {
            for (const feeTier of supportFeeTiers) {
                this.allLinks.push({
                    tokenB,
                    feeContractNumber: toContractFeeNumber(feeTier)
                })
            }
        }

        tokenBList.push({...tokenA})
        tokenBList.push({...tokenB})


        this.pairsOfCalling = []
        this.knownPairs = []

        const poolCalling = [] as string[]

        // pool address calling
        for (let i = 0; i < tokenBList.length; i ++) {
            for (let j = i + 1; j < tokenBList.length; j ++) {
                for (const feeTier of supportFeeTiers) {
                    //const middlePool = (i < tokenBList.length - 2 && j < tokenBList.length - 2)
                    //if (middlePool && feeTier !== 0.01 && feeTier !== 0.04) { continue; }
                    const feeContractNumber = toContractFeeNumber(feeTier)
                    if (!this.hasPool(tokenBList[i], tokenBList[j], feeContractNumber)) {
                        //const poolKey = this.getSwapPoolKey(tokenBList[i], tokenBList[j], feeContractNumber)
                        poolCalling.push(this.liquidityManagerContract.methods.pool(getSwapTokenAddress(tokenBList[i]), getSwapTokenAddress(tokenBList[j]), feeContractNumber).encodeABI())
                        this.pairsOfCalling.push({
                            tokenA: tokenBList[i],
                            tokenB: tokenBList[j],
                            feeContractNumber
                        } as PoolPair)
                    } else {
                        this.knownPairs.push({
                            tokenA: tokenBList[i],
                            tokenB: tokenBList[j],
                            feeContractNumber
                        } as PoolPair)
                    }
                }
            }
        }
        this.responsePoolAddress = []
        this.unknownPoolPoint = []
        for (let i = 0; i < this.pairsOfCalling.length; i ++) {
            this.responsePoolAddress.push('')
            this.unknownPoolPoint.push(0)
        }
        const preQueryResult = this.preQueryResult as iZiPreQueryResult

        const poolCallingNodes: DagNode[] = poolCalling.map((calling: string, idx: number)=>{
            return {
                calling,
                preIdx: undefined,
                targetAddress: this.liquidityManagerAddress,
                parseCallingResponse: (response: string): void => {
                    const address = decodeMethodResult(this.liquidityManagerContract as unknown as Contract, 'pool', response)
                    this.responsePoolAddress[idx] = address
                }
            } as DagNode
        })

        const knownPairStateCalling = [] as string[]
        this.knownPoolPoint = []
        for (let i = 0; i < this.knownPairs.length; i ++) {
            this.knownPoolPoint.push(0)
        }
        // state calling of known pairs
        this.knownPoolAddress = []
        for (const pair of this.knownPairs) {
            const poolKey = this.getSwapPoolKey(pair.tokenA, pair.tokenB, pair.feeContractNumber)
            const poolAddress = preQueryResult.pool.get(poolKey) as string
            knownPairStateCalling.push(this.fakePoolContract.methods.state().encodeABI())
            this.knownPoolAddress.push(poolAddress)
        }

        const knownPairStateCallingNodes: DagNode[] = knownPairStateCalling.map((calling: string, idx: number)=>{
            const poolAddress = this.knownPoolAddress[idx]
            return {
                calling,
                preIdx: undefined,
                targetAddress: poolAddress,
                parseCallingResponse: (response: string): void => {
                    const state: StateResponse = decodeMethodResult(this.fakePoolContract as unknown as Contract, 'state', response) as StateResponse
                    this.knownPoolPoint[idx] = Number(state.currentPoint)
                }
            }
        })

        // state calling of unknown pairs
        const unknownPairStateCallingNodes: DagNode[] = this.pairsOfCalling.map((poolPair: PoolPair, idx: number) => {
            return {
                preIdx: [idx],
                getCallingAndTargetAddress: (): {targetAddress: string, calling: string} => {
                    const poolAddress = this.responsePoolAddress[idx]
                    if (!isAddress(poolAddress) || new BigNumber(poolAddress).eq(0)) {
                        return undefined as unknown as {targetAddress: string, calling: string}
                    }
                    const calling = this.fakePoolContract.methods.state().encodeABI()
                    return {targetAddress: poolAddress, calling}
                },
                parseCallingResponse: (response: string): void => {
                    const state: StateResponse = decodeMethodResult(this.fakePoolContract as unknown as Contract, 'state', response) as StateResponse
                    this.unknownPoolPoint[idx] = Number(state.currentPoint)
                }
            }
        })

        this.dagNodes = [...poolCallingNodes, ...knownPairStateCallingNodes, ...unknownPairStateCallingNodes]

        return this.dagNodes

    }

    override getQueryResult(): PreQueryResult {
        const lastPreQueryResult = this.preQueryResult as iZiPreQueryResult
        const preQueryResult = {
            pathWithFee100: [],
            pathWithOutFee100: [],
            lastChainId: lastPreQueryResult.lastChainId,
            pool: lastPreQueryResult.pool,
            poolPoint: lastPreQueryResult.poolPoint
        } as iZiPreQueryResult
        if (preQueryResult.lastChainId !== this.chainId) {
            preQueryResult.pool = new Map<string, string>()
            preQueryResult.poolPoint = new Map<string, number>()
        } else if (preQueryResult.lastChainId === this.chainId && this.dagNodes.length === 0) {
            preQueryResult.pathWithFee100 = lastPreQueryResult.pathWithFee100
            preQueryResult.pathWithOutFee100 = lastPreQueryResult.pathWithOutFee100
            // no new prequerys
            return preQueryResult
        }
        for (let i = 0; i < this.responsePoolAddress.length; i ++) {
            if (this.responsePoolAddress[i] === '') {
                continue
            }
            const poolAddress = this.responsePoolAddress[i]
            if (!isAddress(poolAddress) || new BigNumber(poolAddress).eq(0)) {
                continue
            }
            const poolPair = this.pairsOfCalling[i]

            const poolKey = this.getSwapPoolKey(poolPair.tokenA, poolPair.tokenB, poolPair.feeContractNumber)
            preQueryResult.pool.set(poolKey, poolAddress)

            preQueryResult.poolPoint.set(poolKey, this.unknownPoolPoint[i])
        }

        for (let i = 0; i < this.knownPairs.length; i ++) {
            const poolPair = this.knownPairs[i]
            const poolKey = this.getSwapPoolKey(poolPair.tokenA, poolPair.tokenB, poolPair.feeContractNumber)
            preQueryResult.poolPoint.set(poolKey, this.knownPoolPoint[i])
        }

        const paths = [] as Path[]

        const supportFeeTiers = SupportFeeTiers[this.chainId]

        for (const i in this.allLinks) {
            const firstKey = this.getSwapPoolKey(this.tokenA, this.allLinks[i].tokenB, this.allLinks[i].feeContractNumber)
            if (!preQueryResult.pool.has(firstKey)) {
                continue
            }
            for (const j in this.allLinks) {
                const lastKey = this.getSwapPoolKey(this.allLinks[j].tokenB, this.tokenB, this.allLinks[j].feeContractNumber)
                if (!preQueryResult.pool.has(lastKey)) {
                    continue
                }
                if (this.allLinks[i].tokenB.symbol === this.allLinks[j].tokenB.symbol) {
                    const feeAB = this.allLinks[i].feeContractNumber
                    const feeBC = this.allLinks[j].feeContractNumber
                    paths.push({
                        tokenChain: [{...this.tokenA}, {...this.allLinks[i].tokenB}, {...this.tokenB}],
                        feeContractNumber: [feeAB, feeBC]
                    } as Path)
                } else {

                    for (const middleFeeTier of supportFeeTiers) {
                        const middleFeeContractNumber = toContractFeeNumber(middleFeeTier)
                        const middleKey = this.getSwapPoolKey(this.allLinks[i].tokenB, this.allLinks[j].tokenB, middleFeeContractNumber)
                        if (!preQueryResult.pool.has(middleKey)) {
                            continue
                        }
                        paths.push({
                            tokenChain: [{...this.tokenA}, {...this.allLinks[i].tokenB}, {...this.allLinks[j].tokenB}, {...this.tokenB}],
                            feeContractNumber: [this.allLinks[i].feeContractNumber, middleFeeContractNumber, this.allLinks[j].feeContractNumber]
                        } as Path)
                    }
                    
                }
            }
        }

        const tokenANormalSymbol = this.getNormalSymbol(this.tokenA.symbol)
        const tokenBNormalSymbol = this.getNormalSymbol(this.tokenB.symbol)
        const lowDirectPoolList = LOW_CHANCE_POOL[this.chainId] ?? []
        const lowDirectPoolSet = new Set<string>()
        for (const pool of lowDirectPoolList) {
            const poolKey = this.getSwapPoolKeySymbol(pool[0] as string, pool[1] as string, toContractFeeNumber(pool[2]))
            lowDirectPoolSet.add(poolKey)
        }
        const date = new Date();

        for (const fee of supportFeeTiers) {
            if (fee === 0.01) {
                continue;
            }
            const feeContractNumber = toContractFeeNumber(fee)
            // use tokenA(B)NormalSymbol
            // to transform eth to weth
            const poolKey = this.getSwapPoolKeySymbol(tokenANormalSymbol, tokenBNormalSymbol, feeContractNumber)
            if (lowDirectPoolSet.has(poolKey)) {
                const hit = (date.getMinutes() % LOW_CHANGE) === 0
                if (!hit) {
                    continue;
                }
            }            
            const directKey = this.getSwapPoolKey(this.tokenA, this.tokenB, feeContractNumber)
            if (!preQueryResult.pool.has(directKey)) {
                continue
            }
            paths.push({
                tokenChain: [{...this.tokenA}, {...this.tokenB}],
                feeContractNumber: [feeContractNumber]
            } as Path)
        }

        const fee001Pools = SUPPORTED_001_POOL[this.chainId];
        const pathWithoutFee100 = [] as Path[]
        const pathWithFee100 = [] as Path[]
        for (const path of paths) {
            let ok = true
            let valid = true
            for (let i=0;  i<path.feeContractNumber.length; i ++) {
                const fee = path.feeContractNumber[i];
                if (fee === 100) {
                    ok = false
                    const tokenA = path.tokenChain[i];
                    const tokenB = path.tokenChain[i+1];
                    const pool = fee001Pools.find((p:any)=> 
                        (p.tokenA.symbol === tokenA.symbol && p.tokenB.symbol === tokenB.symbol) ||
                        (p.tokenB.symbol === tokenA.symbol && p.tokenA.symbol === tokenB.symbol) 
                    )
                    if (!pool){
                        valid = false;
                        break;
                    }
                }
            }
            if (path.feeContractNumber.length === 1) {
                ok= true;
            }
            if (ok) {
                pathWithoutFee100.push(path)
            } else {
                if (valid) {
                    pathWithFee100.push(path)
                }
            }
        }

        preQueryResult.pathWithFee100 = pathWithFee100
        preQueryResult.pathWithOutFee100 = pathWithoutFee100

        return preQueryResult
    }

}
